import { Grow, Menu, MenuItem } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import { useAnchorElState } from 'react-use-object-state';
import { JupiterTheme } from '../theme/types';
import IndicatorChipIcon, { IndicatorChipIconProps } from './IndicatorChipIcon';

const useIndicatorChipStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    container: {
      display: 'flex',
      alignItems: 'center',
      color: theme.palette.background.paper,
      borderRadius: 3,
      height: 28,
      fontSize: 12,
    },
    clickable: {
      '&:hover': {
        cursor: 'pointer',
      },
    },
    labelContainer: {
      flex: '1 1 0',
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(),
    },
    label: {
      flex: '1 1 0',
      textAlign: 'center',
    },
    iconContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      borderRadius: '3px 0px 0px 3px',
      width: 32,
    },
    green: {
      backgroundColor: theme.palette.green[700],
    },
    'dark-green': {
      backgroundColor: theme.palette.green[900],
    },
    red: {
      backgroundColor: theme.palette.red[400],
    },
    'light-red': {
      backgroundColor: theme.palette.red[300],
    },
    'dark-red': {
      backgroundColor: theme.palette.red[600],
    },
    yellow: {
      backgroundColor: theme.palette.yellow[700],
    },
    'dark-yellow': {
      backgroundColor: theme.palette.yellow[800],
    },
    blue: {
      backgroundColor: theme.palette.blue[600],
    },
    'dark-blue': {
      backgroundColor: theme.palette.blue[800],
    },
    gray: {
      backgroundColor: theme.palette.grey[500],
    },
    'dark-gray': {
      backgroundColor: theme.palette.grey[600],
    },
    chevron: {
      width: 14,
      height: 14,
      marginRight: theme.spacing(0.5),
    },
    sm: {
      width: theme.spacing(10),
      maxWidth: theme.spacing(10),
    },
    md: {
      width: theme.spacing(15),
      maxWidth: theme.spacing(15),
    },
    lg: {
      width: theme.spacing(20),
      maxWidth: theme.spacing(20),
    },
    menuLabel: {
      fontSize: 12,
    },
    paper: {
      borderRadius: theme.spacing(0, 0, 0.5, 0.5),
    },
  })
);

export type Color = 'light-red' | 'red' | 'green' | 'blue' | 'yellow' | 'gray';

export interface IndicatorChipProps {
  label: string;
  color: Color;
  badgeText?: string;
  icon?: IndicatorChipIconProps['icon'];
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  menuItems?: {
    label: string;
    icon?: React.ComponentType<any>;
    onClick: VoidFunction;
  }[];
}

export default React.forwardRef<any, IndicatorChipProps>(function IndicatorChip(
  {
    label,
    color,
    icon,
    badgeText,
    size,
    className = '',
    menuItems,
    ...otherProps
  },
  ref
) {
  const classes = useIndicatorChipStyles();
  const colorClass = classes[color];
  const iconColorClass = (classes as Record<string, string>)[`dark-${color}`];

  const iconElement = icon ? <IndicatorChipIcon icon={icon} /> : null;

  const { anchorEl, clearAnchorEl, setAnchorEl } = useAnchorElState(null);

  const hasMenuItems = !!menuItems?.length;

  return (
    <>
      <div
        {...otherProps}
        ref={ref}
        onClick={hasMenuItems ? setAnchorEl : undefined}
        className={clsx(
          classes.container,
          colorClass,
          size && classes[size],
          menuItems?.length && classes.clickable,
          className
        )}
      >
        {iconElement && (
          <div className={clsx(classes.iconContainer, iconColorClass)}>
            {iconElement}
          </div>
        )}
        {badgeText && (
          <div
            className={clsx(
              iconElement && classes.iconContainer,
              iconColorClass
            )}
          >
            {badgeText}
          </div>
        )}
        <div className={classes.labelContainer}>
          <span className={classes.label}>{label}</span>
        </div>
        {hasMenuItems &&
          (anchorEl ? (
            <ChevronUp className={classes.chevron} />
          ) : (
            <ChevronDown className={classes.chevron} />
          ))}
      </div>

      {hasMenuItems && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClick={clearAnchorEl}
          onClose={clearAnchorEl}
          keepMounted
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          getContentAnchorEl={null}
          TransitionComponent={Grow}
          classes={{
            paper: classes.paper,
          }}
        >
          {menuItems?.map(({ onClick, label }) => (
            <MenuItem
              key={label}
              onClick={onClick}
              className={classes.menuLabel}
            >
              {label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
});
